import Image from 'common/components/Image';
import { AppSyncFarm, MonitorVaults } from 'common/models/farm';
import { Table } from 'common/models/table';
import { AppSyncUserInfo } from 'common/models/userInfo';
import debounce from 'lodash/debounce';
import React from 'react';
import { FarmUtils } from 'utils/farmUtils';
import { ScreenUtils } from 'utils/screenUtils';
import { ClaimEvent } from 'xstate/claimEvent';
import {
  claimService,
  EventUpdateAccountInfoArgs,
  initialClaimContext,
} from 'xstate/claimMachine';
import { RemoteTxEvent } from 'xstate/txMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import Tooltip from '../Tooltip/Tooltip';
import VaultsTablePosition from './VaultsTablePosition';
import { TableVirtuoso } from 'react-virtuoso';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { ClaimApi } from 'sdk/claimApi';

type Props = {
  table: Table;
  userInfo: AppSyncUserInfo;
  farms: AppSyncFarm[];
  userInfoLoading: boolean;
  refetchUserInfo: () => Promise<void>;
  isIchiVault: boolean;
  monitorVaults: MonitorVaults[];
  searchValue?: string;
};

const VaultsTable: React.FC<Props> = (props) => {
  const breakpoint = ScreenUtils.useBreakpoint();

  const { chainId } = useSelectedChain();
  const { address: accountAddress } = useAccount();

  const [poolId, setPoolId] = React.useState<number>();
  const [isClaiming, setIsClaiming] = React.useState<boolean>(
    initialClaimContext.isClaiming
  );

  const [visibleRange, setVisibleRange] = React.useState({
    startIndex: 0,
    endIndex: 0,
  });

  const refreshData = async () => {
    await props.refetchUserInfo();
  };

  const debouncedRefreshData = React.useMemo(
    () => debounce(refreshData, 750),
    []
  );

  React.useEffect(() => {
    const subscription = claimService.subscribe((state) => {
      if (state.context.poolId !== poolId) {
        setPoolId(state.context.poolId);
      }

      if (state.context.isClaiming !== isClaiming) {
        setIsClaiming(state.context.isClaiming);
      }
    });

    claimService.onEvent((e) => {
      if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
        void debouncedRefreshData();
      }
    });

    return subscription.unsubscribe;
  }, [claimService, poolId, isClaiming]);

  // Update the account info once it's set
  React.useEffect(() => {
    // Whenever the wallet is connected, update the account info in the claimMachine
    // which may trigger other states, like checking for existing transactions
    if (ClaimApi.Claim.getInstance() && chainId && accountAddress) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress,
        chainId: chainId,
      };
      claimService.send(
        ClaimEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress]);

  const searchCriteria = (vault: AppSyncFarm, searchValue: string) => {
    if (!searchValue) {
      return true;
    }
    return (
      FarmUtils.getBaseToken(vault)
        .displayName.toLowerCase()
        .includes(searchValue.toLowerCase()) ||
      FarmUtils.getBaseToken(vault)
        .fullName.toLowerCase()
        .includes(searchValue.toLowerCase())
    );
  };

  const searchString = props.searchValue ?? '';

  const farms = props.farms.filter((f) => searchCriteria(f, searchString));
  const baseTokens = farms.map((v) => FarmUtils.getBaseToken(v));
  const baseTokenAddresses = baseTokens.map((t) => t.address.toLowerCase());
  const depositTokens = baseTokens.filter(
    (t, index) => baseTokenAddresses.indexOf(t.address.toLowerCase()) === index
  );

  const headers = props.table?.headers?.filter((h) =>
    h.displayOn.includes(breakpoint)
  );
  return (
    <>
      <TableVirtuoso
        rangeChanged={setVisibleRange}
        className="table-vaults-wrapper"
        data={depositTokens}
        components={{
          Table: ({ style, ...props2 }) => (
            <table className="table-vaults" {...props2} style={{ ...style }} />
          ),
        }}
        fixedHeaderContent={() => (
          <tr className="table-border-bottom text-left sub-text-color">
            {headers?.map((header) => (
              <th key={header.id}>
                <div className="flex flex-row">
                  <div className="mr-5">{header.name}</div>
                  {header.helpText && (
                    <div className="tooltip-wrapper">
                      <Tooltip
                        message={header.helpText}
                        position={'right'}
                        bubbleSize={{ width: '180px' }}
                      >
                        <Image className="icon-help" alt={header.helpText} />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        )}
        itemContent={(index, token) => (
          <VaultsTablePosition
            key={token.address}
            token={token}
            farms={farms}
            userInfoLoading={props.userInfoLoading}
            userInfo={props.userInfo}
            claiming={false}
            isIchiVault={props.isIchiVault}
            monitorVaults={props.monitorVaults}
          />
        )}
      />
      <div className="vault-paging primary-text-color">
        Vaults: {visibleRange.startIndex + 1} - {visibleRange.endIndex + 1} of{' '}
        {depositTokens.length}
      </div>
    </>
  );
};

export default VaultsTable;
